import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { DialogFooter } from "@/components/TaskDialogFooter";
import { addTask, updateTask, updateOccurrence, detachOccurrence, deleteTask, restoreDelete, useStore, allCategories, spentSecFor, isTimerFor, setTimeSpent, rememberDelete, getState } from "@/lib/store";
import { toast } from "sonner";
import { RecurrenceEndFields, endFromTask, endToFields } from "@/components/RecurrenceEndFields";

const shiftISO = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + Math.round(days));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function TaskDialog({ open, onClose, defaults, task }) {
  const categories = useStore(allCategories);
  const editing = !!task;
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState(categories[0]?.id || "altro");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [recurrence, setRecurrence] = useState("none");
  const [allDay, setAllDay] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [spentMin, setSpentMin] = useState("0");
  const [spentInitial, setSpentInitial] = useState("0");
  const [showSpent, setShowSpent] = useState(false);
  const timer = useStore((s) => s.timer);
  const [scope, setScope] = useState("single");
  const [end, setEnd] = useState(endFromTask(null));

  const occurrenceDate = task ? (task._occurrenceDate || task.date) : null;
  const isRecurringOccurrence = !!(editing && task.recurrence && task.recurrence !== "none" && occurrenceDate);

  // Sync from `defaults` or `task` each time the dialog opens
  useEffect(() => {
    if (!open) return;
    if (task) {
      setTitle(task.title || "");
      setCategoryId(task.categoryId || categories[0]?.id || "altro");
      setDate(task._occurrenceDate || task.date || "");
      setTime(task.time || "");
      setDuration(task.durationMin || 30);
      setRecurrence(task.recurrence || "none");
      setAllDay(!!task.allDay);
      setEnd(endFromTask(task));
      setScope("single");
      setConfirmingDelete(false);
      const base = getState().tasks.find((t) => t.id === task.id) || task;
      const sec = spentSecFor(base, task._occurrenceDate);
      const live = isTimerFor(timer, task) ? Math.floor((Date.now() - timer.startedAt) / 1000) : 0;
      const min = String(Math.round((sec + live) / 60));
      setSpentMin(min);
      setSpentInitial(min);
      setShowSpent(sec + live > 0);
    } else {
      setTitle("");
      setCategoryId(defaults?.categoryId || categories[0]?.id || "altro");
      setDate(defaults?.date || "");
      setTime(defaults?.time || "");
      setDuration(defaults?.durationMin || 30);
      setRecurrence("none");
      setEnd(endFromTask(null));
      setAllDay(false);
      setConfirmingDelete(false);
      setShowSpent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaults, task, categories]);

  if (!open) return null;

  function doDelete(delScope = "single") {
    const op = deleteTask(task.id, task._occurrenceDate || task.date, isRecurringOccurrence ? delScope : "single");
    if (op) {
      rememberDelete(op);
      const msg = !isRecurringOccurrence ? "Attività eliminata"
        : delScope === "single" ? "Eliminato solo questo giorno"
        : delScope === "future" ? "Eliminato da questo giorno in avanti"
        : "Eliminate tutte le occorrenze";
      toast.success(msg, {
        action: {
          label: "Annulla",
          onClick: () => { restoreDelete(op); toast.success("Attività ripristinata"); }
        },
        duration: 6000
      });
    }
    setConfirmingDelete(false);
    onClose();
  }

  function submit(e) {
    e.preventDefault();
    if (!title.trim()) { toast.error("Scrivi un titolo"); return; }
    const endFields = recurrence !== "none" ? endToFields(end) : { recurrenceUntil: null, recurrenceCount: null };
    const seriesStart = editing ? task.date : date;
    if (endFields.recurrenceUntil && seriesStart && endFields.recurrenceUntil < seriesStart) {
      toast.error("L'ultimo giorno non può essere prima dell'inizio della ricorrenza");
      return;
    }
    const payload = {
      title, categoryId, date: date || null, time: allDay ? null : (time || null),
      durationMin: duration, recurrence, allDay, ...endFields
    };
    if (editing) {
      if (showSpent && String(spentMin) !== spentInitial) setTimeSpent(task.id, occurrenceDate || null, spentMin);
      const recurrenceChanged = (task.recurrence || "none") !== recurrence;
      if (isRecurringOccurrence && scope === "single" && !recurrenceChanged) {
        const endChanged = (task.recurrenceUntil || null) !== endFields.recurrenceUntil
          || (task.recurrenceCount || null) !== endFields.recurrenceCount;
        if (endChanged) updateTask(task.id, endFields);
        const occPatch = { title, categoryId, time: allDay ? null : (time || null), durationMin: duration, allDay };
        if (date && date !== occurrenceDate) {
          detachOccurrence(task.id, occurrenceDate, { ...occPatch, date });
          toast.success("Spostato solo questo giorno");
        } else {
          updateOccurrence(task.id, occurrenceDate, occPatch);
          toast.success("Aggiornato solo questo giorno");
        }
        onClose();
        return;
      }
      if (isRecurringOccurrence && recurrence !== "none") {
        // Whole series edited from one of its days: keep the series start (shift only if the day was changed)
        payload.date = date && date !== occurrenceDate && task.date
          ? shiftISO(task.date, (new Date(date + "T00:00:00") - new Date(occurrenceDate + "T00:00:00")) / 86400000)
          : task.date;
      }
      updateTask(task.id, payload);
      toast.success(isRecurringOccurrence ? "Aggiornate tutte le occorrenze" : "Attività aggiornata");
    } else {
      addTask(payload);
      toast.success("Aggiunta al planner");
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="task-dialog">
      <div className="absolute inset-0 bg-[#3B443D]/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#FFFFFF] rounded-2xl border border-[#E4E7E1] shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E7E1]">
          <div>
            <div className="tl-label">{editing ? "Modifica attività" : "Nuova attività"}</div>
            <div className="font-serif-display text-2xl text-[#3B443D] leading-tight font-semibold">
              {editing ? "Aggiorna l'attività" : "Aggiungi al planner"}
            </div>
          </div>
          <button onClick={onClose} className="text-[#6A7368] hover:text-[#3B443D]" data-testid="dialog-close">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {isRecurringOccurrence && !confirmingDelete && (
            <div className="rounded-xl border border-[#E4E7E1] bg-[#F3F5F0]/60 p-3" data-testid="dialog-scope">
              <div className="tl-label mb-2">Attività ricorrente — applica a</div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setScope("single")}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${scope === "single" ? "bg-[#A3B897] text-white" : "bg-white border border-[#E4E7E1] text-[#58635B]"}`}
                  data-testid="dialog-scope-single"
                >
                  Solo questo giorno
                </button>
                <button
                  type="button"
                  onClick={() => setScope("all")}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${scope === "all" ? "bg-[#A3B897] text-white" : "bg-white border border-[#E4E7E1] text-[#58635B]"}`}
                  data-testid="dialog-scope-all"
                >
                  Tutte le occorrenze
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="tl-label mb-1">Titolo</div>            <input
              autoFocus
              data-testid="dialog-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Cosa devi fare?"
              className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="tl-label mb-1">Categoria</div>
              <select
                data-testid="dialog-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="tl-label mb-1">Giorno</div>
              <input
                data-testid="dialog-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
              />
            </div>
            <div>
              <div className="tl-label mb-1">Orario</div>
              <input
                data-testid="dialog-time"
                type="time"
                disabled={allDay}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897] disabled:opacity-50"
              />
            </div>
            <div>
              <div className="tl-label mb-1">Durata (min)</div>
              <input
                data-testid="dialog-duration"
                type="number"
                min={5} step={5}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
              />
            </div>
            {showSpent && (
              <div className="col-span-2 sm:col-span-1">
                <div className="tl-label mb-1">Tempo impiegato (min)</div>
                <input
                  data-testid="dialog-spent"
                  type="number"
                  min={0} step={1}
                  value={spentMin}
                  onChange={(e) => setSpentMin(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
                />
                <div className="mt-1 text-[11px] text-[#6A7368]">Registrato dal cronometro: correggilo se l'hai dimenticato acceso.</div>
              </div>
            )}
            <div>
              <div className="tl-label mb-1">Ricorrenza</div>
              <select
                data-testid="dialog-recurrence"
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897]"
              >
                <option value="none">Non ricorrente</option>
                <option value="daily">Ogni giorno</option>
                <option value="weekdays">Giorni feriali</option>
                <option value="weekly">Ogni settimana</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm text-[#58635B]">
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="dialog-allday" />
              Tutto il giorno
            </label>
            {recurrence !== "none" && (
              <RecurrenceEndFields value={end} onChange={setEnd} testIdPrefix="dialog" minDate={editing ? task.date : date} />
            )}
          </div>
          <DialogFooter
            editing={editing}
            confirming={confirmingDelete}
            recurring={isRecurringOccurrence}
            onAskDelete={() => setConfirmingDelete(true)}
            onCancelDelete={() => setConfirmingDelete(false)}
            onDelete={doDelete}
            onClose={onClose}
          />
        </form>
      </div>
    </div>
  );
}
